import { useState } from 'react';

import {
  Button,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { SquareDashedIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  IngestionNotice,
  MediaTagsEditor,
  type BrandMediaItem,
  type DetectedObject,
  type IngestionState,
  type MediaFolder,
} from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';

import {
  Field,
  PanelShell,
  Section,
  formatRecordedDate,
  useResizablePanel,
} from './DetailPanelShell';
import { boxColour } from '../lib/box-colours';
import { BoundingBoxOverlay } from './BoundingBoxOverlay';
import { MediaPreviewDialog } from './MediaPreviewDialog';
import { MoveToFolderMenu } from './MoveToFolderMenu';

/** The frame both the still and the player are drawn into, as `aspect-[4/3]` on each. */
const PREVIEW_ASPECT = 4 / 3;

/**
 * Fields the API contract says are always present, read defensively.
 *
 * The type is right about what the endpoint is supposed to send. A locally running backend built
 * before the field existed sends nothing, so the runtime value is undefined while the type says
 * otherwise. This panel crashed twice that way, on `tags` and on optional fields, so every array
 * and object read here goes through a default.
 */
const orEmpty = (value: readonly string[] | undefined): readonly string[] => value ?? [];

const orEmptyObject = (value: Record<string, string[]> | undefined): Record<string, string[]> =>
  value ?? {};

const sectionLabels = (t: TFunction<'tools'>): Record<string, string> => ({
  tags: t('brandMedia.sections.tags'),
  descriptions: t('brandMedia.sections.descriptions'),
  objects: t('brandMedia.sections.objects'),
  properties: t('brandMedia.sections.properties'),
  licence: t('brandMedia.sections.licence'),
});

/**
 * The four numbers as the box they describe: where its corner sits, then how big it is.
 *
 * The stored 0-1 fractions rather than a friendlier unit, so what the panel prints is what the API
 * returns and a value read here can be used as-is. Two decimals because that is the precision the
 * describer actually emits; a third would be invented.
 */
const boxOf = (object: DetectedObject, t: TFunction<'tools'>): string =>
  t('brandMedia.objects.box', {
    x: object.x.toFixed(2),
    y: object.y.toFixed(2),
    w: object.w.toFixed(2),
    h: object.h.toFixed(2),
  });

/**
 * What the library knows about one asset, beside the grid it sits in.
 *
 * Takes the row rather than the grid's tile: a tile is "flattened to what a thumbnail needs" and
 * a grid of many tiles should not carry licence and scene, so this reads the fuller row and the
 * url the grid already resolved for it.
 */
export const MediaDetailPanel = ({
  row,
  url,
  videoUrl,
  ingestion,
  folders,
  isRetryingTagging = false,
  onRetryTagging,
  onClose,
  onMove,
  onRequestDelete,
}: {
  row: BrandMediaItem;
  /** The still: a photograph's own asset, or the poster drawn from a clip. */
  url?: string;
  /**
   * The clip itself, for footage.
   *
   * Absent while the url batch resolves, which the poster covers: this panel falls back to the still
   * rather than showing an empty player.
   */
  videoUrl?: string;
  /**
   * Where ingestion has got to with this row, passed down rather than derived: the grid already
   * decided, and two surfaces deciding separately is how one comes to show a spinner the other does
   * not.
   */
  ingestion?: IngestionState;
  /** Everywhere this asset could be filed, for the footer's move control. */
  folders: readonly MediaFolder[];
  isRetryingTagging?: boolean;
  onRetryTagging?: () => void;
  onClose: () => void;
  /** Raised with the chosen destination, `null` to take the asset out of every folder. */
  onMove: (folderId: string | null) => void;
  /**
   * Raised rather than handled.
   *
   * One dialog and one delete path for the whole library, so the single-versus-several wording and
   * the selection reset afterwards live in one place. This panel stays the reader it is documented
   * to be.
   */
  onRequestDelete: () => void;
}) => {
  const { t } = useTranslation('tools');
  const { canManageBrandAssets } = usePermissions();
  const labels = sectionLabels(t);
  const notRecorded = t('brandMedia.notRecorded');

  const { width, startResize } = useResizablePanel();

  const hasDimensions = row.width != null && row.height != null;
  /**
   * Only when there is nothing to show. A promoted proposal carries the description a person
   * reviewed before it reached the library while ingestion has still to classify the rest of the
   * row, and covering wording somebody wrote with a spinner would be a lie about what is known.
   */
  const awaitingDescriptions =
    ingestion !== undefined && row.literalDescription == null && row.styleDescription == null;
  const markets = orEmpty(row.markets);
  const licenseTiers = orEmpty(row.licenseTiers);
  // Null and undefined collapse here on purpose. The column separates "nothing has looked" from
  // "looked and found nothing", but the first is indistinguishable from a backend too old to send
  // the field at all, and the panel has one honest thing to say about both.
  const objects = row.objects ?? undefined;
  // Pointing previews a box and clicking keeps it, so it can be read against the picture without
  // holding the mouse still. Pointing wins while it lasts, then the pinned one comes back.
  const [pointed, setPointed] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);

  // A pin is a position in one asset's list, so it cannot outlive the asset. The panel is mounted
  // under a constant key and only swaps its `row`, so nothing unmounts this state on a selection
  // change: without this, pinning the fourth box and then opening an asset with two took the whole
  // panel down on `objects[3]`. Adjusted during render rather than in an effect, so no frame is
  // ever painted pointing at the previous asset's box.
  const [pinnedFor, setPinnedFor] = useState(row.id);
  if (pinnedFor !== row.id) {
    setPinnedFor(row.id);
    setPointed(null);
    setPinned(null);
  }

  const highlighted = pointed ?? pinned;

  // Shows every box at once. Pointing at a row still works with it off, so the switch is about
  // seeing the whole reading rather than about turning the feature on.
  const [allBoxesShown, setAllBoxesShown] = useState(false);
  const [enlarged, setEnlarged] = useState(false);

  // The player letterboxes its clip and the still crops its picture, so the overlay has to know
  // which of the two is on screen or it draws the box against the wrong geometry.
  const showsPlayer = row.mediaType === 'video' && videoUrl !== undefined;
  const imageAspect =
    row.width != null && row.height != null && row.height !== 0 ? row.width / row.height : 0;
  const boxes = objects ?? [];
  // Bounded rather than trusted. Resetting on a row change covers the selection case; a re-ingest
  // that shortens this row's list while the panel is open is the same hazard with nothing to key
  // off, so what is drawn is filtered to what exists either way.
  const drawn = allBoxesShown
    ? boxes.map((_object, index) => index)
    : highlighted !== null && highlighted < boxes.length
      ? [highlighted]
      : [];

  return (
    // Its own provider rather than the app's: this panel is shared code, mounted by more than
    // one host, and a tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <PanelShell width={width} testId="media-detail-panel">
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('brandMedia.resizePanel')}
          onPointerDown={startResize}
          className="absolute inset-y-0 left-0 z-10 w-1 -translate-x-1/2 cursor-col-resize touch-none"
        />

        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-light px-lg py-md">
          {/* The same heading treatment the rail's sections carry, so the two columns read as one
              page rather than as a panel that sets type its own way. */}
          <span className="type-heading-sm truncate text-foreground">
            {t('brandMedia.detailPanel')}
          </span>
          {/* What acts on the asset sits in the footer; these two act on the panel itself. The
              switch is drawn only where there is something to switch, so a picture nothing was
              found in does not offer a control that would do nothing. */}
          <div className="flex shrink-0 items-center gap-1">
            {boxes.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Toggle
                    size="sm"
                    pressed={allBoxesShown}
                    onPressedChange={setAllBoxesShown}
                    aria-label={t('brandMedia.objects.toggle')}
                    data-testid="detail-boxes-toggle"
                  >
                    <SquareDashedIcon />
                  </Toggle>
                </TooltipTrigger>
                <TooltipContent>{t('brandMedia.objects.toggle')}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('brandMedia.close')}
                  onClick={onClose}
                >
                  <XIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('brandMedia.close')}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Nothing in here compresses: the column scrolls, so a child that does not fit is reached
            by scrolling to it, not by being squeezed. Without this the flex default shrinks them,
            and the ones carrying `overflow: hidden` -- the truncated title, the font specimen --
            lose the content-based minimum that protects the rest, so a tall picture above squashes
            the title to nothing. */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5 [&>*]:shrink-0">
          {/* The full width of the panel, out to the edges the body's padding would otherwise hold
              it inside, with a rule under it rather than a frame around it. Most of this library is
              packshots on white, and a box drawn around one of those is a box around nothing --
              the one line that has to be there is the one saying where the picture stops and the
              fields begin. Drawn only once there is something to show, or an asset whose url has
              not resolved would leave a stray rule under empty space. */}
          {(url !== undefined || videoUrl !== undefined) && (
            <div className="relative -mx-5 -mt-5 overflow-hidden border-b border-border-light">
              {/* Neither element until a url resolves, same as the grid tile this was opened from. A
                clip whose own url is still in the batch falls back to its poster, so the panel is
                never blank. Checked before the still rather than after, so an image row can never
                render a player even if a stray clip url arrives with it. */}
              {row.mediaType === 'video' && videoUrl !== undefined ? (
                <video
                  data-testid="media-detail-video"
                  src={videoUrl}
                  // The still the grid was already showing, so opening the panel does not flash to
                  // black while the first frame decodes.
                  poster={url}
                  controls
                  // Metadata, not the clip: this panel opens on every single selection, and footage
                  // nobody asked to watch should not download. The grid avoids the same cost by
                  // mounting its preview only on hover.
                  preload="metadata"
                  aria-label={t('brandMedia.videoPlayer', { title: row.title })}
                  // Letterboxed rather than cropped like the still beside it. Footage is commonly
                  // vertical, and cropping it to 4/3 hides most of what someone opened the panel to
                  // judge.
                  className="aspect-[4/3] w-full bg-transparency-checker object-contain"
                >
                  <track kind="captions" />
                </video>
              ) : (
                url !== undefined && (
                  // A button rather than a bare image: opening the full view is a real action and
                  // has to be reachable without a mouse.
                  <button
                    type="button"
                    onClick={() => setEnlarged(true)}
                    aria-label={t('brandMedia.enlarge')}
                    className="block w-full cursor-zoom-in"
                  >
                    <img
                      data-testid="media-detail-image"
                      src={url}
                      alt=""
                      className="aspect-[4/3] w-full object-cover"
                    />
                  </button>
                )
              )}

              {/* Drawn over the picture rather than beside it, because a number and the thing it
                  describes are only checkable against each other. */}
              <BoundingBoxOverlay
                objects={boxes}
                drawn={drawn}
                emphasised={highlighted}
                imageAspect={imageAspect}
                elementAspect={PREVIEW_ASPECT}
                fit={showsPlayer ? 'contain' : 'cover'}
              />
            </div>
          )}

          {/* A step up from the fields below it. This is what the panel is about, and at body size
              it read as one more property of the asset rather than as its name. */}
          <p className="type-body-lg-strong truncate text-foreground" title={row.title}>
            {row.title}
          </p>

          {/* Not wrapped in `Section` like the rest: this one puts controls on its heading line, so
              it renders the whole section itself and takes the label as a prop. */}
          <MediaTagsEditor
            title={labels.tags}
            mediaId={row.id}
            tags={orEmptyObject(row.tags)}
            ingestion={ingestion}
            isRetrying={isRetryingTagging}
            onRetryTagging={onRetryTagging}
          />

          <Section title={labels.descriptions}>
            {/* One notice for the section rather than a "Not recorded" against each field: both
              descriptions are written by the same model call, so they are absent together, and two
              copies of the same sentence would read as two separate problems. No retry here -- the
              Tags section above already carries the one control, and it re-runs the same pass. */}
            {awaitingDescriptions ? (
              <IngestionNotice
                state={ingestion}
                testId="descriptions-ingestion"
                pendingMessage={t('brandMedia.describing')}
                unfinishedMessage={t('brandMedia.notDescribed')}
              />
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <span className="type-label-sm text-foreground-alt">
                    {t('brandMedia.properties.literalDescription')}
                  </span>
                  <p
                    data-testid="detail-literalDescription"
                    className="text-body-md text-foreground"
                  >
                    {row.literalDescription ?? notRecorded}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="type-label-sm text-foreground-alt">
                    {t('brandMedia.properties.styleDescription')}
                  </span>
                  <p data-testid="detail-styleDescription" className="text-body-md text-foreground">
                    {row.styleDescription ?? notRecorded}
                  </p>
                </div>
              </div>
            )}
          </Section>

          {/* Its own section rather than a row under Properties: this is a list of a length nobody
              can predict, and two of a thing are two rows sharing one name -- a shape the
              label-and-value grid beside it does not have. */}
          <Section title={labels.objects}>
            {objects === undefined ? (
              <p data-testid="detail-objects-unknown" className="text-body-md text-foreground-alt">
                {notRecorded}
              </p>
            ) : objects.length === 0 ? (
              <p data-testid="detail-objects-none" className="text-body-md text-foreground-alt">
                {t('brandMedia.objects.none')}
              </p>
            ) : (
              <div className="flex flex-col gap-0.5">
                {objects.map((object, index) => (
                  <div
                    // Indexed because the name is not unique: two people in a shot are two entries
                    // both called `person`, which is exactly what the boxes are here to tell apart.
                    key={`${object.name}-${index}`}
                    data-testid={`detail-object-${index}`}
                    // The same row shape the tag rail uses, for the same reason: pointing at it
                    // has to work from the keyboard too, and a plain div carrying a tabIndex is
                    // not something a screen reader announces as reachable.
                    role="button"
                    tabIndex={0}
                    aria-pressed={pinned === index}
                    onMouseEnter={() => setPointed(index)}
                    onMouseLeave={() =>
                      setPointed((current) => (current === index ? null : current))
                    }
                    onFocus={() => setPointed(index)}
                    onBlur={() => setPointed((current) => (current === index ? null : current))}
                    onClick={() => setPinned((current) => (current === index ? null : index))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setPinned((current) => (current === index ? null : index));
                      }
                    }}
                    className="group flex cursor-pointer flex-col gap-0.5 rounded-sm px-2 py-1 hover:bg-accent focus-visible:bg-accent focus-visible:outline-none aria-pressed:bg-accent"
                  >
                    <span className="flex items-center gap-2">
                      {/* The same colour its box is drawn in, so the list and the picture can be
                          read against each other when several are shown at once. */}
                      <span
                        aria-hidden="true"
                        className="size-2.5 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: boxColour(index) }}
                      />
                      <span className="truncate text-body-md text-foreground">{object.name}</span>
                    </span>
                    {/* Full strength on the row's accent fill, where the quieter tone falls below 4.5:1. */}
                    <span className="pl-[18px] tabular-nums text-body-sm text-foreground-alt group-hover:text-foreground group-focus-visible:text-foreground group-aria-pressed:text-foreground">
                      {boxOf(object, t)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title={labels.properties}>
            <dl className="flex flex-col gap-2">
              <Field
                label={t('brandMedia.properties.dimensions')}
                value={hasDimensions ? `${row.width} x ${row.height}` : notRecorded}
                testId="detail-dimensions"
              />
              <Field
                label={t('brandMedia.properties.orientation')}
                value={row.orientation ?? notRecorded}
                testId="detail-orientation"
              />
              <Field label={t('brandMedia.properties.mediaType')} value={row.mediaType} />
              <Field label={t('brandMedia.properties.placementClass')} value={row.placementClass} />
              <Field
                label={t('brandMedia.properties.scene')}
                value={row.scene ?? notRecorded}
                testId="detail-scene"
              />
              <Field
                label={t('brandMedia.properties.shot')}
                value={row.shot ?? notRecorded}
                testId="detail-shot"
              />
              <Field
                label={t('brandMedia.properties.markets')}
                value={
                  markets.length === 0 ? t('brandMedia.properties.everyMarket') : markets.join(', ')
                }
                testId="detail-markets"
              />
              <Field
                label={t('brandMedia.properties.added')}
                value={formatRecordedDate(row.createdAt, notRecorded)}
                testId="detail-added"
              />
            </dl>
          </Section>

          <Section title={labels.licence}>
            <dl className="flex flex-col gap-2">
              <Field
                label={t('brandMedia.properties.licenseTiers')}
                value={licenseTiers.length === 0 ? notRecorded : licenseTiers.join(', ')}
                testId="detail-licenseTiers"
              />
              <Field
                label={t('brandMedia.properties.licenseExpiry')}
                value={formatRecordedDate(row.licenseExpiry, notRecorded)}
                testId="detail-licenseExpiry"
              />
            </dl>
          </Section>
        </div>

        {/* Outside the scrolling body, so what acts on this asset stays reachable however far down
            the properties someone has read. The two controls are the same pair the bulk bar offers,
            which is what lets that bar stand down for a single selection: file it somewhere, or get
            rid of it.
            Still named for the asset rather than shortened to "Delete", next to a move that names
            its object too -- and it is a destructive verb sitting a few pixels from a dropdown, so
            being specific is worth more than being brief. */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border-light p-5">
          {/* Sized to its own words rather than stretched across the footer. A menu drawn the full
              width of the panel reads as a field waiting to be filled in, and the words it holds
              are the same short pair however wide the panel is dragged. */}
          {canManageBrandAssets ? (
            <MoveToFolderMenu
              folders={folders}
              label={t('brandMedia.moveAsset')}
              currentFolderId={row.folderId}
              className="shrink-0"
              onMove={onMove}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="outline" size="sm" className="shrink-0" disabled>
                    {t('brandMedia.moveAsset')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('brandMediaLibrary.permissions.noPermission')}</TooltipContent>
            </Tooltip>
          )}
          {/* Ghost rather than a filled destructive: this sits in a footer that is on screen for
              as long as an asset is open, so a solid red bar reads as a warning about the asset
              rather than as one of two things you can do to it. The confirmation is what guards
              the delete. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  disabled={!canManageBrandAssets}
                  onClick={onRequestDelete}
                >
                  <Trash2Icon />
                  {t('brandMedia.deleteAsset')}
                </Button>
              </span>
            </TooltipTrigger>
            {!canManageBrandAssets && (
              <TooltipContent>{t('brandMediaLibrary.permissions.noPermission')}</TooltipContent>
            )}
          </Tooltip>
        </div>

        {/* Carries the panel's own switch in rather than starting fresh, so opening the full view
            does not silently drop the boxes somebody had just turned on. */}
        {url !== undefined && (
          <MediaPreviewDialog
            open={enlarged}
            title={row.title}
            url={url}
            width={row.width ?? null}
            height={row.height ?? null}
            objects={boxes}
            boxesShown={allBoxesShown}
            onBoxesShownChange={setAllBoxesShown}
            onOpenChange={setEnlarged}
          />
        )}
      </PanelShell>
    </TooltipProvider>
  );
};
